/**
 * React Native port of frontend/client/src/Components/Orb.
 *
 * Key decisions:
 *  - Each ring lives in its own Animated.View so React Native's native
 *    transform driver handles rotation + scale (smooth 60 fps, JS-free).
 *  - OrbCore is an Animated.View (opacity + scale) wrapping a tiny SVG that
 *    renders the radial-gradient glow sphere — matching the web's separate
 *    <OrbCore> div that sits behind the SVG rings.
 *  - No background, no overflow:hidden — the web OrbWrapper has neither.
 *  - Drop-shadow approximated with iOS shadow props on each ring view.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Platform, StyleSheet, View } from 'react-native';
import Svg, { Circle, Defs, Path, RadialGradient, Stop } from 'react-native-svg';
import { useTheme } from '../hooks/useTheme';
import { hexToRgba } from '../utilities/color';

// Brand-blue fallback used when the theme doesn't expose a ring color. The
// shared `hexToRgba` returns '' for missing input so we apply the default
// upstream and let the helper just convert.
const ORB_RING_FALLBACK = '#00B4FF';

const RING1 =
  'M371.9,201.7c0,0,0,1-0.1,3c-0.1,2-0.4,4.9-1.2,8.6c-0.8,3.7-2.3,8.2-4.6,13.2c-2.3,5-5.5,10.5-9.4,16.4' +
  'c-3.9,5.9-8.6,12.2-13.6,19.2c-2.5,3.5-5,7.1-7.4,11c-2.4,3.9-4.7,8.1-6.8,12.5c-4.3,8.9-8.1,18.6-12.8,28.7' +
  'c-2.4,5-5,10.2-8.1,15.2c-3.1,5-6.8,9.9-11.4,14.2c-2.3,2.1-4.8,4.1-7.5,5.8l-1,0.6c-0.2,0.1-0.3,0.2-0.6,0.3l-0.5,0.3' +
  'c-0.7,0.4-1.3,0.8-2.1,1.1c-0.7,0.4-1.5,0.7-2.2,1.1c-0.8,0.3-1.5,0.6-2.3,0.9c-6.1,2.3-12.7,3.4-19.4,3.8' +
  'c-6.7,0.4-13.4,0.1-20.2-0.5c-6.8-0.6-13.6-1.3-20.5-2c-6.9-0.7-13.9-1.2-21-1.1c-7,0.1-14.2,0.7-21.5,1.3' +
  'c-7.3,0.7-14.7,1.4-22.2,1.7c-7.5,0.3-15.2,0.2-22.8-1.3c-7.6-1.4-15-4.4-21.3-9.2c-6.3-4.8-11.4-10.9-15.6-17.6' +
  'c-8.4-13.4-13.7-28.4-20.7-42.7c-1.8-3.6-3.6-7.1-5.7-10.5c-2.1-3.4-4.3-6.7-6.6-10c-4.6-6.5-9.6-12.9-14.3-19.5' +
  'c-4.7-6.6-9.1-13.4-12.5-20.9c-3.4-7.4-5.6-15.5-5.6-23.9c-0.1-8.2,2-16.5,5.3-24c3.3-7.5,7.7-14.5,12.3-21.2' +
  'c4.6-6.7,9.5-13.1,14.1-19.6c2.3-3.3,4.5-6.6,6.5-10c2-3.4,3.8-6.9,5.5-10.5c6.9-14.2,12.2-29.3,20.7-42.8' +
  'c4.3-6.7,9.4-13,15.9-17.8c0.8-0.6,1.6-1.2,2.5-1.7c0.9-0.5,1.7-1.1,2.6-1.6c0.9-0.5,1.6-0.9,2.6-1.4c0.9-0.4,1.8-0.9,2.8-1.3' +
  'c3.7-1.5,7.5-2.6,11.4-3.4c7.7-1.4,15.5-1.5,23-1.2c7.5,0.4,14.9,1.2,22.2,1.9c7.3,0.7,14.4,1.4,21.5,1.5l2.6,0l2.7,0' +
  'c1.7,0,3.5-0.1,5.2-0.2c3.5-0.2,7-0.5,10.4-0.8c13.8-1.2,27.4-3.1,40.8-2.3c6.7,0.4,13.3,1.6,19.4,3.9c6.1,2.3,11.6,5.9,16.2,10.2' +
  'c4.6,4.3,8.3,9.2,11.4,14.2c3.1,5,5.7,10.1,8.1,15.2c4.7,10.1,8.5,19.9,12.8,28.7c2.1,4.4,4.4,8.6,6.8,12.5c2.4,3.9,5,7.5,7.4,11' +
  'c4.9,7,9.7,13.3,13.6,19.2c4,5.9,7.1,11.4,9.4,16.4c2.3,5,3.7,9.5,4.6,13.2c0.8,3.7,1.1,6.6,1.2,8.6' +
  'C371.9,200.6,371.9,201.6,371.9,201.7L371.9,201.7z ' +
  'M371.5,201.6c0,0,0-1-0.1-3c-0.1-1.9-0.4-4.8-1.3-8.5' +
  'c-0.9-3.7-2.3-8.2-4.6-13.1c-2.3-5-5.6-10.4-9.6-16.2c-2-2.9-4.2-5.9-6.5-9.1c-2.4-3.1-4.8-6.4-7.4-9.8c-2.6-3.4-5.2-7-7.7-10.9' +
  'c-2.5-3.9-4.9-8.1-7.2-12.5c-4.5-8.8-8.6-18.6-13.5-28.4c-2.4-4.9-5.1-9.9-8.3-14.7c-3.2-4.8-6.8-9.4-11.1-13.3' +
  'c-1.1-1-2.2-1.9-3.4-2.8c-0.6-0.5-1.2-0.9-1.8-1.3c-0.6-0.4-1.2-0.8-1.9-1.2c-2.6-1.6-5.3-2.9-8.1-3.9c-5.7-2.1-12-3.1-18.4-3.4' +
  'c-12.8-0.6-26.3,1.2-40.2,2.6c-3.5,0.3-7,0.6-10.6,0.8c-1.8,0.1-3.6,0.2-5.4,0.2l-2.7,0l-2.7,0c-7.3-0.1-14.5-0.8-21.8-1.5' +
  'c-7.3-0.7-14.6-1.5-22-1.9c-7.4-0.3-14.8-0.3-22,1c-3.6,0.7-7.2,1.6-10.6,3c-0.9,0.3-1.7,0.7-2.5,1.1c-0.8,0.3-1.7,0.9-2.5,1.3' +
  'c-0.7,0.4-1.6,0.9-2.3,1.4c-0.8,0.5-1.5,1-2.3,1.6c-6,4.4-10.9,10.3-15,16.7c-8.3,13-13.7,27.9-20.8,42.3' +
  'c-1.8,3.6-3.7,7.2-5.8,10.7c-2.1,3.5-4.4,6.8-6.7,10.1c-4.7,6.6-9.7,12.9-14.3,19.4c-4.7,6.5-9,13.3-12.4,20.5' +
  'c-3.3,7.2-5.4,15-5.5,23c-0.1,7.9,1.9,15.8,5.1,23.1c3.2,7.3,7.5,14.2,12.1,20.8c4.6,6.6,9.5,13,14.2,19.6' +
  'c2.3,3.3,4.6,6.6,6.6,10.1c2.1,3.5,3.9,7,5.7,10.6c3.5,7.2,6.6,14.5,9.9,21.7c3.3,7.2,6.8,14.2,10.9,20.8c4.2,6.5,9.2,12.5,15.3,17' +
  'c6.1,4.5,13.3,7.2,20.6,8.5c7.4,1.3,14.9,1.3,22.3,0.9c7.4-0.4,14.7-1.3,22-2.1c7.3-0.8,14.5-1.5,21.8-1.7' +
  'c7.2-0.2,14.4,0.3,21.3,0.9c13.9,1.2,27.3,3.1,40.2,2.4c6.4-0.3,12.7-1.4,18.4-3.5c0.7-0.3,1.4-0.6,2.1-0.8c0.7-0.3,1.4-0.6,2-0.9' +
  'c0.7-0.3,1.4-0.7,2.1-1.1l0.5-0.3c0.1,0,0.3-0.2,0.4-0.3l1-0.6c2.5-1.6,4.9-3.4,7.1-5.3c4.4-4,8-8.6,11.2-13.4' +
  'c3.1-4.8,5.8-9.8,8.3-14.7c4.9-9.9,8.9-19.6,13.4-28.4c2.2-4.4,4.6-8.6,7.2-12.5c2.5-3.9,5.2-7.5,7.7-10.9c2.6-3.4,5-6.7,7.4-9.8' +
  'c2.3-3.1,4.5-6.2,6.5-9.1c4-5.8,7.3-11.3,9.6-16.3c2.3-5,3.8-9.4,4.6-13.1c0.8-3.7,1.1-6.6,1.3-8.5' +
  'C371.5,202.7,371.5,201.6,371.5,201.6L371.5,201.6z';

const RING2 =
  'M328.4,328.7c0,0-0.8,0.7-2.3,2.2c-1.6,1.4-3.9,3.3-7.3,5.5c-3.4,2.1-7.8,4.4-13.3,6.4' +
  'c-5.5,2-12,3.7-19.3,5.2c-7.3,1.4-15.6,2.6-24.4,4c-4.4,0.7-9.1,1.5-13.8,2.6c-4.7,1.1-9.5,2.5-14.4,4.2c-9.8,3.4-20,7.8-31,11.8' +
  'c-5.5,2-11.3,3.8-17.3,5.2c-6,1.3-12.4,2.2-19,2c-3.3-0.1-6.6-0.5-9.8-1.3l-1.2-0.3c-0.2-0.1-0.4-0.1-0.6-0.2l-0.6-0.2' +
  'c-0.8-0.2-1.6-0.4-2.4-0.7c-0.8-0.3-1.6-0.6-2.4-0.9c-0.8-0.3-1.6-0.7-2.4-1c-6.2-2.8-11.9-6.9-17.2-11.5' +
  'c-5.2-4.6-10-9.9-14.6-15.4c-4.6-5.5-9.1-11.2-13.8-16.8c-4.7-5.6-9.5-11.3-14.9-16.5c-5.3-5.2-11.2-10.1-17.1-15' +
  'c-5.9-4.9-12-9.9-17.8-15.2c-5.8-5.4-11.4-11.2-16-17.9c-4.5-6.7-7.9-14.4-9-22.6c-1.1-8.2-0.3-16.5,1.5-24.6' +
  'c3.7-16.2,11-31.4,16.4-47.2c1.4-4,2.6-8,3.6-12.1c1-4.1,1.8-8.2,2.5-12.4c1.4-8.3,2.5-16.8,3.9-25.2c1.4-8.4,3.3-16.8,6.3-24.9' +
  'c3-8,7.5-15.7,13.6-21.9c6.1-6.1,13.8-10.7,21.8-13.8c8.1-3.1,16.5-5,24.9-6.5c8.4-1.5,16.8-2.6,25.2-4c4.2-0.7,8.3-1.5,12.3-2.6' +
  'c4-1,8-2.3,12-3.6c15.8-5.5,31-12.7,47.4-16.3c8.1-1.8,16.6-2.6,24.9-1.4c1,0.2,2.1,0.4,3.1,0.5c1,0.3,2,0.5,3.1,0.7' +
  'c1,0.3,1.9,0.5,2.9,0.9c1,0.4,2,0.7,3,1.1c3.8,1.6,7.5,3.6,10.8,5.9c6.8,4.7,12.6,10.3,17.9,16.2c5.3,5.9,10.2,12,15.1,18' +
  'c4.9,6,9.7,11.8,14.9,17.2c5.2,5.4,10.7,10.2,16.3,14.9c11.2,9.4,22.7,18.1,32,28.6c4.6,5.3,8.7,11,11.5,17.2' +
  'c2.8,6.2,4.2,12.9,4.4,19.5c0.2,6.6-0.7,12.9-2,19c-1.4,6-3.3,11.8-5.3,17.3c-4,11-8.5,21.2-11.8,31c-1.7,4.9-3.1,9.7-4.2,14.4' +
  'c-1.1,4.7-1.9,9.3-2.7,13.8c-1.5,8.9-2.7,17.1-4.1,24.4c-1.4,7.3-3.2,13.8-5.2,19.3c-2,5.5-4.3,9.9-6.5,13.3' +
  'c-2.2,3.4-4.1,5.8-5.5,7.3C329.1,328,328.4,328.7,328.4,328.7z ' +
  'M328.2,328.5c0,0,0.7-0.8,2.1-2.3c1.3-1.6,3.3-3.9,5.4-7.3' +
  'c2.1-3.4,4.4-7.8,6.3-13.2c2-5.4,3.7-11.9,5.1-19.2c0.7-3.7,1.3-7.5,2-11.6c0.6-4.1,1.2-8.4,1.9-12.8c0.7-4.5,1.4-9.1,2.4-13.9' +
  'c1-4.8,2.4-9.7,4-14.6c3.3-9.9,7.5-20.2,11.3-31.2c1.9-5.5,3.6-11.2,4.9-17.1c1.2-5.9,2-12.1,1.7-18.4c-0.3-6.3-1.7-12.6-4.4-18.5' +
  'c-2.7-5.9-6.6-11.4-11.2-16.4c-9.1-10.1-20.5-18.8-31.8-28.2c-2.8-2.4-5.7-4.8-8.4-7.3c-1.4-1.3-2.8-2.5-4.2-3.9l-2-2l-2-2' +
  'c-5.3-5.5-10.2-11.4-15.1-17.4c-4.9-6-9.8-12-15-17.8c-5.2-5.8-10.8-11.3-17.3-15.8c-3.2-2.2-6.6-4.2-10.3-5.7' +
  'c-0.9-0.4-1.8-0.7-2.7-1c-0.9-0.3-1.9-0.6-2.9-0.9c-0.9-0.2-1.9-0.5-2.8-0.7c-1-0.2-1.9-0.4-2.9-0.5c-7.8-1.2-15.9-0.5-23.9,1.2' +
  'c-15.9,3.5-31.1,10.6-47.1,16c-4,1.3-8.1,2.6-12.2,3.6c-4.1,1-8.3,1.8-12.5,2.5c-8.4,1.4-16.8,2.4-25.2,3.8' +
  'c-8.4,1.4-16.7,3.2-24.6,6.2c-7.9,3-15.3,7.2-21.3,13.2c-6,5.9-10.4,13.3-13.5,21.2c-3.1,7.9-5,16.2-6.5,24.6' +
  'c-1.5,8.4-2.6,16.8-4,25.1c-0.7,4.2-1.6,8.3-2.6,12.5c-1,4.1-2.3,8.1-3.7,12.1c-2.8,8-5.9,15.7-8.8,23.5' +
  'c-2.9,7.8-5.6,15.6-7.4,23.7c-1.7,8-2.5,16.2-1.3,24.2c1.2,8,4.5,15.4,9,21.9c4.5,6.5,10.1,12.1,15.9,17.4' +
  'c5.8,5.2,11.9,10.1,17.9,14.9c6,4.9,11.9,9.7,17.4,15c5.5,5.3,10.4,10.9,15.1,16.6c9.4,11.3,18.1,22.7,28.2,31.8' +
  'c5.1,4.6,10.5,8.5,16.4,11.2c0.7,0.3,1.5,0.6,2.2,1c0.7,0.3,1.5,0.6,2.2,0.8c0.7,0.3,1.6,0.5,2.3,0.7l0.6,0.2' +
  'c0.1,0,0.4,0.1,0.6,0.1l1.2,0.3c3.1,0.7,6.3,1.1,9.4,1.3c6.3,0.3,12.5-0.5,18.4-1.7c5.9-1.3,11.6-3,17.2-4.9' +
  'c11-3.8,21.3-8,31.2-11.2c5-1.6,9.9-3,14.6-4c4.8-1,9.4-1.7,13.9-2.4c4.5-0.6,8.7-1.2,12.8-1.8c4.1-0.6,8-1.2,11.6-1.9' +
  'c7.3-1.4,13.8-3,19.3-5c5.5-1.9,9.9-4.2,13.3-6.3c3.4-2.1,5.8-4.1,7.3-5.4C327.4,329.3,328.2,328.5,328.2,328.5z';

const RING3 =
  'M328.7,74.9c0,0,0.7,0.8,2.2,2.3c1.4,1.6,3.3,3.9,5.5,7.3c2.1,3.4,4.4,7.8,6.4,13.3c2,5.5,3.7,12,5.2,19.3' +
  'c1.4,7.3,2.6,15.6,4,24.4c0.7,4.4,1.5,9.1,2.6,13.8c1.1,4.7,2.5,9.5,4.2,14.4c3.4,9.8,7.8,20,11.8,31c2,5.5,3.8,11.3,5.2,17.3' +
  'c1.3,6,2.2,12.4,2,19c-0.1,3.3-0.5,6.6-1.3,9.8l-0.3,1.2c-0.1,0.2-0.1,0.4-0.2,0.6l-0.2,0.6c-0.2,0.8-0.4,1.6-0.7,2.4' +
  'c-0.3,0.8-0.6,1.6-0.9,2.4c-0.3,0.8-0.7,1.6-1,2.4c-2.8,6.2-6.9,11.9-11.5,17.2c-4.6,5.2-9.9,10-15.4,14.6' +
  'c-5.5,4.6-11.2,9.1-16.8,13.8c-5.6,4.7-11.3,9.5-16.5,14.9c-5.2,5.3-10.1,11.2-15,17.1c-4.9,5.9-9.9,12-15.2,17.8' +
  'c-5.4,5.8-11.2,11.4-17.9,16c-6.7,4.5-14.4,7.9-22.6,9c-8.2,1.1-16.5,0.3-24.6-1.5c-16.2-3.7-31.4-11-47.2-16.4' +
  'c-4-1.4-8-2.6-12.1-3.6c-4.1-1-8.2-1.8-12.4-2.5c-8.3-1.4-16.8-2.5-25.2-3.9c-8.4-1.4-16.8-3.3-24.9-6.3c-8-3-15.7-7.5-21.9-13.6' +
  'c-6.2-6.1-10.7-13.8-13.8-21.8c-3.1-8.1-5-16.5-6.5-24.9c-1.5-8.4-2.6-16.8-4-25.2c-0.7-4.2-1.5-8.3-2.6-12.3c-1-4-2.3-8-3.6-12' +
  'c-5.5-15.8-12.7-31-16.3-47.4c-1.8-8.1-2.6-16.6-1.4-24.9c0.2-1,0.4-2.1,0.5-3.1c0.3-1,0.5-2,0.7-3.1c0.3-1,0.5-1.9,0.9-2.9' +
  'c0.4-1,0.7-2,1.1-3c1.6-3.8,3.6-7.5,5.9-10.8c4.7-6.8,10.3-12.6,16.2-17.9c5.9-5.3,12-10.2,18-15.1c6-4.9,11.8-9.7,17.2-14.9' +
  'c5.4-5.2,10.2-10.7,14.9-16.3c9.4-11.2,18.1-22.7,28.6-32c5.3-4.6,11-8.7,17.2-11.5c6.2-2.8,12.9-4.2,19.5-4.4' +
  'c6.6-0.2,12.9,0.7,19,2c6,1.4,11.8,3.3,17.3,5.3c11,4,21.2,8.5,31,11.8c4.9,1.7,9.7,3.1,14.4,4.2c4.7,1.1,9.3,1.9,13.8,2.7' +
  'c8.9,1.5,17.1,2.7,24.4,4.1c7.3,1.4,13.8,3.2,19.3,5.2c5.5,2,9.9,4.3,13.3,6.5c3.4,2.2,5.8,4.1,7.3,5.5' +
  'C328,74.2,328.7,74.9,328.7,74.9z ' +
  'M328.5,75.1c0,0-0.8-0.7-2.3-2.1c-1.6-1.3-3.9-3.3-7.3-5.4c-3.4-2.1-7.8-4.4-13.2-6.3' +
  'c-5.4-2-11.9-3.7-19.2-5.1c-3.7-0.7-7.5-1.3-11.6-2c-4.1-0.6-8.4-1.2-12.8-1.9c-4.5-0.7-9.1-1.4-13.9-2.4c-4.8-1-9.7-2.4-14.6-4' +
  'c-9.9-3.3-20.2-7.5-31.2-11.3c-5.5-1.9-11.2-3.6-17.1-4.9c-5.9-1.2-12.1-2-18.4-1.7c-6.3,0.3-12.6,1.7-18.5,4.4' +
  'C142.4,35,137,39,131.9,43.5c-10.1,9.1-18.8,20.5-28.2,31.8c-2.4,2.8-4.8,5.7-7.3,8.4c-1.3,1.4-2.5,2.8-3.9,4.2l-2,2l-2,2' +
  'c-5.5,5.3-11.4,10.2-17.4,15.1c-6,4.9-12,9.8-17.8,15c-5.8,5.2-11.3,10.8-15.8,17.3c-2.2,3.2-4.2,6.6-5.7,10.3' +
  'c-0.4,0.9-0.7,1.8-1,2.7c-0.3,0.9-0.6,1.9-0.9,2.9c-0.2,0.9-0.5,1.9-0.7,2.8c-0.2,1-0.4,1.9-0.5,2.9c-1.2,7.8-0.5,15.9,1.2,23.9' +
  'c3.5,15.9,10.6,31.1,16,47.1c1.3,4,2.6,8.1,3.6,12.2c1,4.1,1.8,8.3,2.5,12.5c1.4,8.4,2.4,16.8,3.8,25.2c1.4,8.4,3.2,16.7,6.2,24.6' +
  'c3,7.9,7.3,15.3,13.2,21.3c5.9,6,13.3,10.4,21.2,13.5c7.9,3.1,16.2,5,24.6,6.5c8.4,1.5,16.8,2.6,25.1,4c4.2,0.7,8.3,1.6,12.5,2.6' +
  'c4.1,1,8.1,2.3,12.1,3.7c8,2.8,15.7,5.9,23.5,8.8c7.8,2.9,15.6,5.6,23.7,7.4c8,1.7,16.2,2.5,24.2,1.3c8-1.2,15.4-4.5,21.9-9' +
  'c6.5-4.5,12.1-10.1,17.4-15.9c5.2-5.8,10.1-11.9,14.9-17.9c4.9-6,9.7-11.9,15-17.4c5.3-5.5,10.9-10.4,16.6-15.1' +
  'c11.3-9.4,22.7-18.1,31.8-28.2c4.6-5.1,8.5-10.5,11.2-16.4c0.3-0.7,0.6-1.5,1-2.2c0.3-0.7,0.6-1.5,0.8-2.2c0.3-0.7,0.5-1.6,0.7-2.3' +
  'l0.2-0.6c0-0.1,0.1-0.4,0.1-0.6l0.3-1.2c0.7-3.1,1.1-6.3,1.3-9.4c0.3-6.3-0.5-12.5-1.7-18.4c-1.3-5.9-3-11.6-4.9-17.2' +
  'c-3.8-11-8-21.3-11.2-31.2c-1.6-5-3-9.9-4-14.6c-1-4.8-1.7-9.4-2.4-13.9c-0.6-4.5-1.2-8.7-1.8-12.8c-0.6-4.1-1.2-8-1.9-11.6' +
  'c-1.4-7.3-3-13.8-5-19.3c-1.9-5.5-4.2-9.9-6.3-13.3c-2.1-3.4-4.1-5.8-5.4-7.3C329.3,75.9,328.5,75.1,328.5,75.1z';

const CENTER = 201.65;
const CORE_R = 55; // 403.3 * ~0.136 ≈ 55 — matches web's size * 0.3 / 2

interface OrbAvatarProps {
  size?: number;
  /** Primary ring colour — defaults to theme.colors.electricBlue (web: theme.electricBlue) */
  color?: string;
  /** Secondary ring colour — defaults to theme.colors.primary (web: theme.buttonBackground) */
  color2?: string;
  thinking?: boolean;
  /**
   * Stronger, faster pulse used while the assistant is speaking out loud.
   * Larger amplitude than `thinking` so the orb visually carries the audio.
   */
  speaking?: boolean;
  /**
   * Active mic listening (STT). Drives a level-reactive pulse based on
   * `listeningLevel` (0..1). When true but no level is provided we fall back
   * to a gentle ambient pulse so the user always sees the orb is "alive".
   */
  listening?: boolean;
  /** Normalized 0..1 mic level (e.g. from speech.ts onVolume). */
  listeningLevel?: number;
  style?: object;
}

export default function OrbAvatar({
  size = 28,
  color,
  color2,
  thinking = false,
  speaking = false,
  listening = false,
  listeningLevel = 0,
  style,
}: OrbAvatarProps) {
  const theme = useTheme();
  // Mirror web defaults: color = electricBlue, color2 = buttonBackground (primary)
  const ringColor = color ?? theme.colors.electricBlue;
  const ringColor2 = color2 ?? theme.colors.primary;

  // ─── Rotation — linear, continuous ─────────────────────────────────────────
  const rot1 = useRef(new Animated.Value(0)).current;
  const rot2 = useRef(new Animated.Value(0)).current;
  const rot3 = useRef(new Animated.Value(0)).current;

  // ─── Scale pulse per ring ────────────────────────────────────────────────────
  const scale1 = useRef(new Animated.Value(1)).current;
  const scale2 = useRef(new Animated.Value(1)).current;
  const scale3 = useRef(new Animated.Value(1)).current;

  // ─── OrbCore ─────────────────────────────────────────────────────────────────
  const coreScale   = useRef(new Animated.Value(1)).current;
  const coreOpacity = useRef(new Animated.Value(0.6)).current;

  // ─── Breathing (thinking mode) ───────────────────────────────────────────────
  const breatheScale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const d1 = 10000, d2 = 10000, d3 = 7000;
    const ease = Easing.inOut(Easing.sin);

    // Decorative loops must not hold InteractionManager open: on web that
    // prevents FlatList from rendering beyond its first ten message rows.
    // Rotation — native driver where supported.
    const r1 = Animated.loop(Animated.timing(rot1, { toValue: 1, duration: d1, easing: Easing.linear, useNativeDriver: true, isInteraction: false }));
    const r2 = Animated.loop(Animated.timing(rot2, { toValue: 1, duration: d2, easing: Easing.linear, useNativeDriver: true, isInteraction: false }));
    const r3 = Animated.loop(Animated.timing(rot3, { toValue: 1, duration: d3, easing: Easing.linear, useNativeDriver: true, isInteraction: false }));

    // Scale pulse — native driver
    const s1 = Animated.loop(Animated.sequence([
      Animated.timing(scale1, { toValue: 1.05, duration: d1 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale1, { toValue: 1,    duration: d1 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale1, { toValue: 1.05, duration: d1 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale1, { toValue: 1,    duration: d1 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
    ]));
    const s2 = Animated.loop(Animated.sequence([
      Animated.timing(scale2, { toValue: 0.95, duration: d2 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale2, { toValue: 1,    duration: d2 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale2, { toValue: 1.05, duration: d2 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale2, { toValue: 1,    duration: d2 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
    ]));
    const s3 = Animated.loop(Animated.sequence([
      Animated.timing(scale3, { toValue: 1.05, duration: d3 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale3, { toValue: 1,    duration: d3 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale3, { toValue: 1.05, duration: d3 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
      Animated.timing(scale3, { toValue: 1,    duration: d3 / 4, easing: ease, useNativeDriver: true, isInteraction: false }),
    ]));

    // Core glow pulse — native driver. While `speaking`, the pulse runs faster
    // and reaches a larger scale/opacity so the orb visually carries the audio.
    const corePeakScale   = speaking ? 1.25 : 1.15;
    const corePeakOpacity = speaking ? 1.0  : 0.9;
    const coreDuration    = speaking ? 600  : 1250;
    const core = Animated.loop(Animated.sequence([
      Animated.parallel([
        Animated.timing(coreScale,   { toValue: corePeakScale,   duration: coreDuration, easing: ease, useNativeDriver: true, isInteraction: false }),
        Animated.timing(coreOpacity, { toValue: corePeakOpacity, duration: coreDuration, easing: ease, useNativeDriver: true, isInteraction: false }),
      ]),
      Animated.parallel([
        Animated.timing(coreScale,   { toValue: 1,   duration: coreDuration, easing: ease, useNativeDriver: true, isInteraction: false }),
        Animated.timing(coreOpacity, { toValue: 0.6, duration: coreDuration, easing: ease, useNativeDriver: true, isInteraction: false }),
      ]),
    ]));

    // Breathing — runs in thinking OR speaking mode, with a larger amplitude
    // when speaking so the whole orb appears to "swell" with each phrase.
    // While `listening` we DON'T run a timed loop here — the parent feeds
    // breatheScale directly via listeningLevel (see separate effect below).
    const breatheAmp = speaking ? 1.05 : 1.02;
    const breatheDuration = speaking ? 700 : 1500;
    const breathe = (thinking || speaking) && !listening
      ? Animated.loop(Animated.sequence([
          Animated.timing(breatheScale, { toValue: breatheAmp, duration: breatheDuration, easing: ease, useNativeDriver: true, isInteraction: false }),
          Animated.timing(breatheScale, { toValue: 1,          duration: breatheDuration, easing: ease, useNativeDriver: true, isInteraction: false }),
        ]))
      : null;

    r1.start(); r2.start(); r3.start();
    s1.start(); s2.start(); s3.start();
    core.start();
    breathe?.start();

    return () => {
      r1.stop(); r2.stop(); r3.stop();
      s1.stop(); s2.stop(); s3.stop();
      core.stop();
      breathe?.stop();
      if (!thinking && !speaking && !listening) breatheScale.setValue(1);
    };
  }, [thinking, speaking, listening]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mic-level reactive pulse — animates the breathing scale toward a target
  // derived from listeningLevel (0..1). Smooth tween rather than `setValue`
  // so the orb glides between samples instead of jumping. Only active while
  // `listening` is true; otherwise the timed breathe loop above takes over.
  useEffect(() => {
    if (!listening) return;
    const target = 1 + Math.max(0, Math.min(1, listeningLevel)) * 0.18; // up to 1.18
    const anim = Animated.timing(breatheScale, {
      toValue: target,
      duration: 120,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    });
    anim.start();
    return () => anim.stop();
  }, [listening, listeningLevel, breatheScale]);

  // Interpolate 0→1 to degree strings for native transform
  const rot1Deg = rot1.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });
  const rot2Deg = rot2.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '-360deg'] });
  const rot3Deg = rot3.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '-360deg'] });

  const opR1 = speaking ? 1.0  : listening ? 0.9  : thinking ? 0.9  : 0.75;
  const opR2 = speaking ? 0.95 : listening ? 0.85 : thinking ? 0.85 : 0.7;
  const opR3 = speaking ? 1.0  : listening ? 0.9  : thinking ? 0.9  : 0.78;

  // ── Glow shadow ─────────────────────────────────────────────────────────────
  // Native: `shadowColor`/`shadowOffset`/`shadowRadius` rendered per-ring; iOS
  // honors the SVG path alpha so the halo is round. Keeping the shadow OFF the
  // natively-animated views (rotation/scale) avoids CoreAnimation interpolating
  // shadow props alongside transforms, which produces NaN → CoreGraphics spam.
  //
  // Web: those same RN shadow props translate to CSS `box-shadow`, which
  // respects the View's bounding box (a square), so the per-ring halo renders
  // as three stacked rectangular cyan blocks — exactly the "weird" glow visible
  // in browser/Electron screenshots. Switch to a single `filter: drop-shadow()`
  // on the outer wrapper instead — drop-shadow follows the alpha of the
  // composited content, giving us the round halo the SVG paths actually want.
  const shadowOpacity = speaking
    ? 0.7
    : listening
      ? 0.4 + Math.max(0, Math.min(1, listeningLevel)) * 0.3
      : thinking
        ? 0.5
        : 0.3;
  const shadowRadius = size * (speaking ? 0.4 : listening ? 0.35 : 0.3);

  const ringGlowStyle = Platform.OS === 'web'
    ? { backgroundColor: 'transparent' as const }
    : {
        shadowColor: ringColor,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity,
        shadowRadius,
        backgroundColor: 'transparent' as const,
      };

  const webOuterFilter = useMemo(() => {
    if (Platform.OS !== 'web') return undefined;
    const rgba = hexToRgba(ringColor || ORB_RING_FALLBACK, shadowOpacity);
    return `drop-shadow(0 0 ${shadowRadius}px ${rgba})`;
  }, [ringColor, shadowOpacity, shadowRadius]);

  const absRing = {
    position: 'absolute' as const,
    width: size,
    height: size,
    backgroundColor: 'transparent' as const,
  };

  return (
    <Animated.View
      style={[
        { width: size, height: size, backgroundColor: 'transparent', transform: [{ scale: breatheScale }] },
        // Web: outer drop-shadow renders one round halo that follows the SVG
        // ring alpha. Native ignores `filter` (RN doesn't read it) and uses
        // the per-ring shadow props in `ringGlowStyle` below.
        Platform.OS === 'web' && webOuterFilter ? ({ filter: webOuterFilter } as any) : null,
        style,
      ]}
    >
      {/* ── OrbCore: radial-gradient sphere behind the rings ── */}
      <Animated.View
        style={[absRing, { opacity: coreOpacity, transform: [{ scale: coreScale }] }]}
      >
        <Svg viewBox="0 0 403.3 403.3" width={size} height={size} style={{ backgroundColor: 'transparent' }}>
          <Defs>
            <RadialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
              <Stop offset="0%"   stopColor={ringColor} stopOpacity={1} />
              <Stop offset="30%"  stopColor={ringColor} stopOpacity={0.67} />
              <Stop offset="60%"  stopColor={ringColor} stopOpacity={0.27} />
              <Stop offset="100%" stopColor={ringColor} stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Circle cx={CENTER} cy={CENTER} r={CORE_R} fill="url(#coreGlow)" />
        </Svg>
      </Animated.View>

      {/* ── Ring 1: clockwise ── */}
      {/* Shadow on static wrapper; transforms on inner Animated.View */}
      <View style={[absRing, ringGlowStyle]}>
        <Animated.View
          style={[StyleSheet.absoluteFill, { backgroundColor: 'transparent', opacity: opR1, transform: [{ rotate: rot1Deg }, { scale: scale1 }] }]}
        >
          <Svg viewBox="0 0 403.3 403.3" width={size} height={size} style={{ backgroundColor: 'transparent' }}>
            <Path d={RING1} fill={ringColor} stroke={ringColor} strokeWidth={1} fillRule="evenodd" />
          </Svg>
        </Animated.View>
      </View>

      {/* ── Ring 2: counter-clockwise (uses color2) ── */}
      <View style={[absRing, ringGlowStyle]}>
        <Animated.View
          style={[StyleSheet.absoluteFill, { backgroundColor: 'transparent', opacity: opR2, transform: [{ rotate: rot2Deg }, { scale: scale2 }] }]}
        >
          <Svg viewBox="0 0 403.3 403.3" width={size} height={size} style={{ backgroundColor: 'transparent' }}>
            <Path d={RING2} fill={ringColor2} stroke={ringColor2} strokeWidth={1} fillRule="evenodd" />
          </Svg>
        </Animated.View>
      </View>

      {/* ── Ring 3: counter-clockwise, faster ── */}
      <View style={[absRing, ringGlowStyle]}>
        <Animated.View
          style={[StyleSheet.absoluteFill, { backgroundColor: 'transparent', opacity: opR3, transform: [{ rotate: rot3Deg }, { scale: scale3 }] }]}
        >
          <Svg viewBox="0 0 403.3 403.3" width={size} height={size} style={{ backgroundColor: 'transparent' }}>
            <Path d={RING3} fill={ringColor} stroke={ringColor} strokeWidth={1} fillRule="evenodd" />
          </Svg>
        </Animated.View>
      </View>
    </Animated.View>
  );
}
